import { createSlice } from "@reduxjs/toolkit";
import {
  createCategoryThunk,
  getAllCategoriesThunk,
  getCategoryByIdThunk,
  updateCategoryByIdThunk,
  deleteCategoryByIdThunk,
  updateCategoryOrderThunk,
  syncCategoryStatsThunk,
  getCategoriesForDropdownThunk,
  getCategoryTreeThunk,
  getCategoriesByBranchThunk,
  setCategoryPricingForBranchThunk,
  removeCategoryPricingForBranchThunk,
  getCategoryPricingRulesThunk,
  getCategoryPricingInfoThunk,
  bulkUpdateProductsPricingThunk,
  getCategoryPricingAuditThunk,
  getAllPricingAuditsThunk,
  clearCategoryError,
  setSelectedCategory,
  toggleCategoryExpansion,
  clearPricingAudit,
} from "../thunks/categoryThunks";

const initialState = {
  categories: [],
  categoryTree: [],
  dropdownCategories: [],
  selectedCategory: null,
  expandedCategories: [], // For tree view UI state

  // Pricing state
  pricingRules: null,
  pricingInfo: null,
  pricingAudit: [],
  pricingStats: null,

  filters: {
    parentId: null,
    productType: null,
    isActive: true,
    branchCode: null,
    storeCode: null,
  },

  stats: {
    totalCategories: 0,
    activeCategories: 0,
    totalProducts: 0,
  },

  loading: false,
  treeLoading: false,
  dropdownLoading: false,
  pricingLoading: false,
  error: null,
  lastSync: null,
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Category
      .addCase(createCategoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.push(action.payload);
        state.stats.totalCategories += 1;
        if (action.payload.display?.isActive) {
          state.stats.activeCategories += 1;
        }
      })
      .addCase(createCategoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get All Categories
      .addCase(getAllCategoriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCategoriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        const data = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
        state.categories = data;
        state.stats.totalCategories = data.length;
        state.stats.activeCategories = data.filter(
          (cat) => cat.display?.isActive
        ).length;
        state.stats.totalProducts = data.reduce(
          (sum, cat) => sum + (cat.productCount || 0),
          0
        );
      })
      .addCase(getAllCategoriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Category By ID
      .addCase(getCategoryByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategoryByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCategory = action.payload;
      })
      .addCase(getCategoryByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Category
      .addCase(updateCategoryByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategoryByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(
          (category) => category._id === action.payload._id
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        if (state.selectedCategory?._id === action.payload._id) {
          state.selectedCategory = action.payload;
        }
      })
      .addCase(updateCategoryByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Category
      .addCase(deleteCategoryByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategoryByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload.deletedId || action.payload._id;
        state.categories = state.categories.filter(
          (category) => category._id !== deletedId
        );
        if (state.selectedCategory?._id === deletedId) {
          state.selectedCategory = null;
        }
        state.stats.totalCategories -= 1;
      })
      .addCase(deleteCategoryByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Category Order
      .addCase(updateCategoryOrderThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategoryOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.categories.sort((a, b) => a.order - b.order);
        }
      })
      .addCase(updateCategoryOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Sync Category Stats
      .addCase(syncCategoryStatsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncCategoryStatsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSync = new Date().toISOString();
      })
      .addCase(syncCategoryStatsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Categories for Dropdown
      .addCase(getCategoriesForDropdownThunk.pending, (state) => {
        state.dropdownLoading = true;
        state.error = null;
      })
      .addCase(getCategoriesForDropdownThunk.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        const data = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
        state.dropdownCategories = data;
      })
      .addCase(getCategoriesForDropdownThunk.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.error = action.payload;
      })

      // Get Category Tree
      .addCase(getCategoryTreeThunk.pending, (state) => {
        state.treeLoading = true;
        state.error = null;
      })
      .addCase(getCategoryTreeThunk.fulfilled, (state, action) => {
        state.treeLoading = false;
        const data = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
        state.categoryTree = data;
      })
      .addCase(getCategoryTreeThunk.rejected, (state, action) => {
        state.treeLoading = false;
        state.error = action.payload;
      })

      // Get Categories by Branch
      .addCase(getCategoriesByBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategoriesByBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        const data = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
        state.categories = data;
      })
      .addCase(getCategoriesByBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== PRICING CASES ==========

      // Set Category Pricing for Branch
      .addCase(setCategoryPricingForBranchThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(setCategoryPricingForBranchThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        // Update category if it exists in state
        const category = state.categories.find(
          (cat) => cat._id === action.payload.category?.id
        );
        if (category && category.pricingConfig) {
          // Update the pricing config
          const branchIndex =
            category.pricingConfig.branchSpecificRules?.findIndex(
              (rule) => rule.branchCode === action.payload.branchCode
            );
          if (branchIndex >= 0) {
            category.pricingConfig.branchSpecificRules[branchIndex] = {
              branchCode: action.payload.branchCode,
              pricingRuleId: action.payload.pricingRule.id,
              appliedFrom: new Date().toISOString(),
            };
          } else {
            if (!category.pricingConfig.branchSpecificRules) {
              category.pricingConfig.branchSpecificRules = [];
            }
            category.pricingConfig.branchSpecificRules.push({
              branchCode: action.payload.branchCode,
              pricingRuleId: action.payload.pricingRule.id,
              appliedFrom: new Date().toISOString(),
            });
          }
        }
      })
      .addCase(setCategoryPricingForBranchThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // Remove Category Pricing for Branch
      .addCase(removeCategoryPricingForBranchThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(
        removeCategoryPricingForBranchThunk.fulfilled,
        (state, action) => {
          state.pricingLoading = false;
          // Update category if it exists in state
          const category = state.categories.find(
            (cat) => cat.name === action.payload.category
          );
          if (category && category.pricingConfig?.branchSpecificRules) {
            category.pricingConfig.branchSpecificRules =
              category.pricingConfig.branchSpecificRules.filter(
                (rule) => rule.branchCode !== action.payload.branchCode
              );
          }
        }
      )
      .addCase(
        removeCategoryPricingForBranchThunk.rejected,
        (state, action) => {
          state.pricingLoading = false;
          state.error = action.payload;
        }
      )

      // Get Category Pricing Rules
      .addCase(getCategoryPricingRulesThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(getCategoryPricingRulesThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        state.pricingRules = action.payload;
      })
      .addCase(getCategoryPricingRulesThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // Get Category Pricing Info
      .addCase(getCategoryPricingInfoThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(getCategoryPricingInfoThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        state.pricingInfo = action.payload;
      })
      .addCase(getCategoryPricingInfoThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // Bulk Update Products Pricing
      .addCase(bulkUpdateProductsPricingThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateProductsPricingThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        // Could show a notification with the update results
      })
      .addCase(bulkUpdateProductsPricingThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // Get Category Pricing Audit
      .addCase(getCategoryPricingAuditThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(getCategoryPricingAuditThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        state.pricingAudit = action.payload;
      })
      .addCase(getCategoryPricingAuditThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // Get All Pricing Audits
      .addCase(getAllPricingAuditsThunk.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(getAllPricingAuditsThunk.fulfilled, (state, action) => {
        state.pricingLoading = false;
        if (action.payload.totalChanges !== undefined) {
          // It's stats data
          state.pricingStats = action.payload;
        } else {
          // It's audit logs
          state.pricingAudit = action.payload;
        }
      })
      .addCase(getAllPricingAuditsThunk.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload;
      })

      // UI Actions
      .addCase(clearCategoryError, (state) => {
        state.error = null;
      })
      .addCase(setSelectedCategory, (state, action) => {
        state.selectedCategory = action.payload;
      })
      .addCase(toggleCategoryExpansion, (state, action) => {
        const categoryId = action.payload;
        const index = state.expandedCategories.indexOf(categoryId);
        if (index === -1) {
          state.expandedCategories.push(categoryId);
        } else {
          state.expandedCategories.splice(index, 1);
        }
      })
      .addCase(clearPricingAudit, (state) => {
        state.pricingAudit = [];
        state.pricingStats = null;
      });
  },
});

export const { setFilters, resetFilters, clearSelectedCategory } =
  categorySlice.actions;
export default categorySlice.reducer;
